import React from 'react';
import { REPROV_AGING_REPORT } from '../constants/ApiEndpoints';
import { makeGetRequest } from '../helpers/biztechAxiosRequest';
import moment from 'moment';
import { DD_MMM_YYYY_HH_MM_SS_A_Z } from '../constants/DateTimeFormats';
import loadingGif from '../assets/loading.gif';

declare global {
  namespace JSX {
    interface IntrinsicElements {
      's-table-container': React.HTMLAttributes<HTMLElement> & {
        tabindex?: string;
      };
    }
  }
}

interface AgingReportEntry {
  crmId: string;
  name?: string;
  camName?: string;
  lastReprovisionedAt: string;
  reprovisionAge: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    milliseconds: number;
  };
  deprovisionedDatesLast60Days?: string[];
  entitlementsReprovisioned?: {
    features: { id: string; name: string; serviceId: string }[];
    systemId: string;
    additionalInfo: { key: string; value: string }[];
    deliverySystemName: string;
  }[];
  ticketId?: string;
}

type SortField = 'crmId' | 'lastReprovisionedAt' | 'agingDays';
type SortDir = 'asc' | 'desc';

type Env = 'prd' | 'sbx';

interface State {
  rows: AgingReportEntry[];
  loading: boolean;
  errorMessage: string;
  sortField: SortField;
  sortDir: SortDir;
  env: Env;
  selectedEntitlements: AgingReportEntry['entitlementsReprovisioned'] | null;
}

function highlightJson(json: string): string {
  return json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      match => {
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            return `<span style="color:#0451a5">${match}</span>`;  // key — dark blue
          }
          return `<span style="color:#a31515">${match}</span>`;    // string — dark red
        }
        if (/true|false/.test(match)) {
          return `<span style="color:#0000ff">${match}</span>`;    // boolean — blue
        }
        if (/null/.test(match)) {
          return `<span style="color:#0000ff">${match}</span>`;    // null — blue
        }
        return `<span style="color:#098658">${match}</span>`;      // number — dark green
      }
    );
}

class ReprovAgingReport extends React.Component<{}, State> {
  state: State = {
    rows: [],
    loading: false,
    errorMessage: '',
    sortField: 'lastReprovisionedAt',
    sortDir: 'desc',
    selectedEntitlements: null,
    env: 'prd'
  };

  componentDidMount() {
    this.fetchReport();
  }

  fetchReport = async () => {
    this.setState({ loading: true, errorMessage: '' });
    try {
      const res = await makeGetRequest(
        `${REPROV_AGING_REPORT}?env=${this.state.env}`
      );
      this.setState({ rows: res.data ?? [] });
    } catch {
      this.setState({ errorMessage: 'Failed to load aging report.' });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleEnvChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const env = event.target.value as Env;
    this.setState({ env }, this.fetchReport);
  };

  handleSort = (field: SortField) => {
    this.setState(prev => ({
      sortField: field,
      sortDir:
        prev.sortField === field && prev.sortDir === 'asc' ? 'desc' : 'asc'
    }));
  };

  sortedRows(): AgingReportEntry[] {
    const { rows, sortField, sortDir } = this.state;
    return [...rows].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortField === 'agingDays') {
        aVal = a.reprovisionAge?.days ?? 0;
        bVal = b.reprovisionAge?.days ?? 0;
      } else if (sortField === 'lastReprovisionedAt') {
        aVal = new Date(a.lastReprovisionedAt).getTime();
        bVal = new Date(b.lastReprovisionedAt).getTime();
      } else {
        aVal = a.crmId ?? '';
        bVal = b.crmId ?? '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  sortIcon(field: SortField) {
    const { sortField, sortDir } = this.state;
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  render() {
    const { loading, errorMessage } = this.state;

    if (loading) {
      return (
        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <img src={loadingGif} alt="loading..." />
        </div>
      );
    }

    const rows = this.sortedRows();

    return (
      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16
          }}
        >
          <h2 style={{ margin: 0 }}>Reprov Aging Report</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label htmlFor="env-filter" style={{ fontWeight: 600 }}>
              Environment
            </label>
            <select
              id="env-filter"
              name="env"
              value={this.state.env}
              onChange={this.handleEnvChange}
            >
              <option value="prd">Production</option>
              <option value="sbx">Sandbox</option>
            </select>
            <button className="secondary" onClick={this.fetchReport}>
              Refresh
            </button>
          </div>
        </div>

        <s-table-container tabindex="0">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {[
                  { label: 'CRM ID', field: 'crmId' as SortField },
                  { label: 'Name' },
                  { label: 'CAM Name' },
                  { label: 'Last Reprovisioned At', field: 'lastReprovisionedAt' as SortField },
                  { label: 'Aging (days)', field: 'agingDays' as SortField },
                  { label: 'Ticket ID' },
                  { label: 'Entitlements Reprovisioned' },
                  { label: 'Deprovisioned Dates (Last 60 Days)' },
                ].map(({ label, field }) => (
                  <th
                    key={label}
                    onClick={field ? () => this.handleSort(field) : undefined}
                    style={{
                      padding: '12px 16px',
                      border: '1px solid #c8ccd0',
                      backgroundColor: '#e8f0fe',
                      color: '#1a3c6e',
                      fontWeight: 700,
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      ...(field ? { cursor: 'pointer', userSelect: 'none' as const } : {}),
                    }}
                  >
                    {label}{field ? this.sortIcon(field) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    style={{ textAlign: 'center', padding: 24, border: '1px solid #c8ccd0' }}
                  >
                    {errorMessage ? errorMessage : 'No data available.'}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => {
                  return (
                    <tr
                      key={row.crmId}
                      style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f7f9fc' }}
                    >
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        <a
                          href={`/#/search?query=${encodeURIComponent(row.crmId)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="Link"
                        >
                          {row.crmId}
                        </a>
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        {row.name ?? '-'}
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        {row.camName ?? '-'}
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        {row.lastReprovisionedAt
                          ? moment(row.lastReprovisionedAt).format(DD_MMM_YYYY_HH_MM_SS_A_Z)
                          : '-'}
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0', textAlign: 'center' }}>
                        {row.reprovisionAge?.days ?? '-'}
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        {row.ticketId ?? '-'}
                      </td>
                      <td
                        style={{ padding: '10px 16px', border: '1px solid #c8ccd0', cursor: 'pointer' }}
                        onClick={() => this.setState({ selectedEntitlements: row.entitlementsReprovisioned ?? null })}
                        title="Click to view full JSON"
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div>
                            {row.entitlementsReprovisioned?.length
                              ? row.entitlementsReprovisioned.map((e, i) => (
                                  <div key={i} style={{ marginBottom: i < row.entitlementsReprovisioned!.length - 1 ? 8 : 0 }}>
                                    <strong>{e.deliverySystemName}</strong>:{' '}
                                    {e.features.map(f => f.name).join(', ')}
                                  </div>
                                ))
                              : '-'}
                          </div>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" style={{ flexShrink: 0, opacity: 0.5 }} fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zm0 12.5c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                        </div>
                      </td>
                      <td style={{ padding: '10px 16px', border: '1px solid #c8ccd0' }}>
                        {row.deprovisionedDatesLast60Days?.length
                          ? row.deprovisionedDatesLast60Days.map(d => (
                              <div key={d}>{moment(d).format(DD_MMM_YYYY_HH_MM_SS_A_Z)}</div>
                            ))
                          : '-'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </s-table-container>

        {this.state.selectedEntitlements && (
          <div
            style={{
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', zIndex: 1000
            }}
            onClick={() => this.setState({ selectedEntitlements: null })}
          >
            <div
              style={{
                background: '#fff', borderRadius: 8, padding: 24, minWidth: '40vw', maxWidth: '65vw',
                maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 24px rgba(0,0,0,0.2)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <strong style={{ fontSize: 15, color: '#1a3c6e' }}>Entitlements Reprovisioned</strong>
                <button
                  className="secondary small"
                  onClick={() => this.setState({ selectedEntitlements: null })}
                >
                  Close
                </button>
              </div>
              <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '16px', border: '1px solid #e0e0e0' }}>
                <pre
                  style={{ margin: 0, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', textAlign: 'left' }}
                  dangerouslySetInnerHTML={{ __html: highlightJson(JSON.stringify(this.state.selectedEntitlements, null, 2)) }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default ReprovAgingReport;
